import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { Card } from '../ui/Card';

interface Props {
  title: string;
  /** Route the "View all" link points at. Omit to render the header without a link. */
  viewAllHref?: string;
  children: ReactNode;
}

/**
 * Shared chrome for the dashboard list panels that sit under the KPI tiles: a titled card with
 * a "View all" escape hatch to the full screen. The panels themselves own their rows.
 */
export function DashboardPanel({ title, viewAllHref, children }: Props) {
  const { t } = useTranslation();
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b">
        <h2 className="text-sm font-semibold">{title}</h2>
        {viewAllHref && (
          <Link
            to={viewAllHref}
            className="text-xs text-primary hover:underline inline-flex items-center gap-1"
          >
            {t('dashboard.viewAll')} <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>
      {children}
    </Card>
  );
}

/** Centred placeholder used when a panel has nothing to list. */
export function PanelEmpty({ children }: { children: ReactNode }) {
  return <div className="px-5 py-10 text-center text-sm text-muted-foreground">{children}</div>;
}
